/**
 * The FRONT half of the row formula — a live preview, never the truth.
 *
 * The database computes the stored value (`GENERATED ALWAYS AS`, compiled in
 * the backend's `structure/domain/formula.ts`); this mirror exists so the
 * subtotal MOVES while the person is still typing the quantity, instead of
 * appearing only after save. Two implementations of one grammar is the same
 * bargain the onboarding took with djb2: both ends must agree, and the server
 * end wins whenever they do not.
 *
 * Same semantics as the compiled SQL, deliberately:
 * - any operand empty → the result is empty (SQL NULL propagation),
 * - division by zero or by empty → empty (the SQL wraps divisors in NULLIF),
 * - `coalesce(a, b, …)` → the first non-empty argument.
 *
 * Anything that does not parse — an expression from a newer grammar, a field
 * this row does not carry — answers `undefined`, and the caller falls back to
 * the stored value. A preview must never be louder than the data.
 */

export type FormulaScope = Record<string, unknown>;

/** `null` = the formula computed "empty"; `undefined` = it could not compute. */
export function evaluateFormula(expr: string, scope: FormulaScope): number | null | undefined {
	if (expr.length > 500) return undefined;
	const parser = new Parser(expr, scope);
	const value = parser.expression();
	if (parser.failed || !parser.atEnd()) return undefined;
	return value;
}

class Parser {
	failed = false;
	private cursor = 0;

	constructor(
		private readonly expr: string,
		private readonly scope: FormulaScope,
	) {}

	atEnd(): boolean {
		this.skipSpace();
		return this.cursor >= this.expr.length;
	}

	expression(): number | null {
		let left = this.term();
		this.skipSpace();
		while (!this.failed && (this.peek() === "+" || this.peek() === "-")) {
			const op = this.take();
			const right = this.term();
			left = combine(left, right, (a, b) => (op === "+" ? a + b : a - b));
		}
		return left;
	}

	private term(): number | null {
		let left = this.unary();
		this.skipSpace();
		while (!this.failed && (this.peek() === "*" || this.peek() === "/")) {
			const op = this.take();
			const right = this.unary();
			left =
				op === "/"
					? combine(left, right === 0 ? null : right, (a, b) => a / b)
					: combine(left, right, (a, b) => a * b);
		}
		return left;
	}

	private unary(): number | null {
		this.skipSpace();
		if (this.peek() === "-") {
			this.take();
			const value = this.unary();
			return value === null ? null : -value;
		}
		return this.primary();
	}

	private primary(): number | null {
		this.skipSpace();
		if (this.peek() === "(") {
			this.take();
			const inner = this.expression();
			if (!this.expect(")")) return null;
			return inner;
		}

		const number = /^\d+(\.\d+)?/.exec(this.expr.slice(this.cursor));
		if (number) {
			this.cursor += number[0].length;
			return Number(number[0]);
		}

		const word = /^[a-zA-Z_][a-zA-Z0-9_]*/.exec(this.expr.slice(this.cursor));
		if (!word) {
			this.failed = true;
			return null;
		}
		this.cursor += word[0].length;
		const lower = word[0].toLowerCase();

		if (lower === "coalesce") return this.coalesce();
		this.skipSpace();
		if (this.peek() === "(") {
			this.failed = true;
			return null;
		}
		return numberOf(this.scope[lower]);
	}

	private coalesce(): number | null {
		if (!this.expect("(")) return null;
		let found: number | null = this.expression();
		this.skipSpace();
		let count = 1;
		while (!this.failed && this.peek() === ",") {
			this.take();
			const next = this.expression();
			if (found === null) found = next;
			count += 1;
			this.skipSpace();
		}
		if (count < 2) this.failed = true;
		if (!this.expect(")")) return null;
		return found;
	}

	private expect(char: string): boolean {
		this.skipSpace();
		if (this.peek() !== char) {
			this.failed = true;
			return false;
		}
		this.take();
		return true;
	}

	private peek(): string {
		return this.expr[this.cursor] ?? "";
	}

	private take(): string {
		const char = this.expr[this.cursor] ?? "";
		this.cursor += 1;
		return char;
	}

	private skipSpace(): void {
		while (/\s/.test(this.expr[this.cursor] ?? "")) this.cursor += 1;
	}
}

function combine(
	a: number | null,
	b: number | null,
	op: (a: number, b: number) => number,
): number | null {
	if (a === null || b === null) return null;
	const result = op(a, b);
	return Number.isFinite(result) ? result : null;
}

/** A form value as a number — "" and null are EMPTY, not zero (§3.8). */
function numberOf(value: unknown): number | null {
	if (value === null || value === undefined || value === "") return null;
	if (typeof value === "number") return Number.isFinite(value) ? value : null;
	if (typeof value === "string") {
		const parsed = Number(value.replace(",", "."));
		return Number.isFinite(parsed) ? parsed : null;
	}
	return null;
}
